<template lang="pug">
.theindex-post
  .heading-container.main-container
    .category(v-if="post.category.name !== 'Uncategorized'") {{ __(post.category.name) }}
    h1(v-html="post.title.rendered" v-translate="THE_INDEX_TITLES_NS")
    .author {{author}}
      .date
        span.published(v-if="post.date") {{ `${__('Published')}: ${publishedDate}` }}
        span(v-if="showModifiedDate") &nbsp;&vert;&nbsp;
        span.modified(v-if="showModifiedDate") {{ `${__('Updated')}: ${modifiedDate}` }}
    social-share.left(v-if="!$rogue.appContext")
  .gallery-content(@click="clickGallery($event)" ref="contentRef")
    post-content(
      :content="renderedContent"
      :namespace="`theindex_${post.id}`"
      container-class="content main-container"
    )
  CarouselModal(v-if="showImages" :value="showImgIndex" @close="showImages = false")
    img.slide-img(v-for="(img, i) in images" :src="img" :key="img" :alt="__('Gallery image {n}', { n: String(i + 1) })")
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { THE_INDEX_TITLES_NS } from '../constants'
import postContent from '../components/post-content.vue'
import socialShare from '@/js/components/social-share.vue'
import { formatDate } from '@/js/helpers/date'
import { useRogue } from '@/js/services/Rogue'

const props = defineProps({
  post: {
    type     : Object,
    required : true,
  },
  authorList: {
    type    : Array,
    default : () => ([]),
  },
})

const rogue = useRogue()
const images = ref<string[]>([])
const showImgIndex = ref<number | null>(null)
const showImages = ref(false)
const contentRef = ref<HTMLElement>()

const renderedContent = computed(() => props.post.content.rendered.replace(/<style type='text\/css'>.*<\/style>/sig, ''))
const publishedDate = computed<string>(() => formatDate(props.post.date, rogue))
const modifiedDate = computed<string>(() => formatDate(props.post.modified, rogue))
const showModifiedDate = computed<boolean>(() => publishedDate.value !== modifiedDate.value)

const author = computed<string | null>(() => {
  const name = props.post._embedded?.author?.[0]?.name
  if (!name || !props.authorList.length) return null
  const lower = name.toLowerCase()

  return props.authorList.some(auth => lower.includes(auth)) ? name : null
})

// Scoped to gallery items so an injected carousel's product shots stay out of the lightbox. Held
// alongside `images` so a clicked photo can be located by identity rather than by counting
// siblings, which is what keeps the two in step.
let galleryImages: HTMLImageElement[] = []

function collectImages(): void {
  galleryImages = Array.from(contentRef.value?.querySelectorAll<HTMLImageElement>('.gallery-item img') || [])
  images.value = galleryImages.map(img => img.getAttribute('src')?.replace('-810x605', '') || '')
}

function clickGallery(e: Event) {
  const item = (e.target as HTMLElement).closest('.gallery-item')
  if (!item) {
    return
  }

  // The lightbox renders one flat list of slides, so the index has to count across every gallery
  // in the post — the clicked item's position among its own siblings restarts at 0 in each one.
  const index = galleryImages.indexOf(item.querySelector('img') as HTMLImageElement)
  if (index === -1) {
    return
  }

  e.preventDefault()
  e.stopPropagation()
  showImgIndex.value = index
  showImages.value = true
}

onMounted(collectImages)

// Routing between gallery posts reuses this component, so the images have to be gathered again
// once the new body is in the DOM; onMounted alone would leave the lightbox on the previous post.
watch(renderedContent, async () => {
  showImages.value = false
  await nextTick()
  collectImages()
})
</script>

<style lang="scss">
.theindex-post .gallery {
  display: flex;
  flex-wrap: wrap;
}

.theindex-post .gallery-item {
  position: relative;
  margin: 0 2px 2px 0;
  overflow: hidden;

  @include mobile() {
    flex-basis: calc(50% - 2px);
  }

  @include tablet() {
    flex-basis: calc(33.333% - 2px);
  }

  @include all-desktop() {
    flex-basis: calc(25% - 2px);
  }
}

.theindex-post .gallery-item:after {
  content: "";
  display: block;
  padding-bottom: 100%;
}

.theindex-post .gallery-item img {
  position: absolute;
  width: auto;
  height: 125%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.slide-img {
  max-height: 90%;
  max-width: 90%;
}

.date{
  font-size: $size-xs;
  color: $color-gray-x-dark;

  .published, .modified {
    font-style: italic;    
  }
}
.author{
  font-style: italic;
}
</style>
